"use client";

import { useState } from "react";
import { Funnel as Filter, Heart } from 'lucide-react';
import { AddToCartButton, EcommerceProductsProvider, ProductSortDropdown, ProductsFilter, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
const current_wp_query_params = typeof window !== "undefined" && (window as any).wvcClient ? (window as any).wvcClient.getCurrentWPQueryParams() : {};
const wp_query = new WP_Query(current_wp_query_params);
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    loadMore,
    isRefetching
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[3/4] rounded-lg bg-muted/40 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground">
        Failed to load products.{" "}
        <Button variant="link" onClick={refetch} className="px-1 text-primary underline">
          Retry
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="font-default text-2xl text-muted-foreground italic">
        No products available at this time.
      </p>;
  }
  return <>
      <div className="grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => <article key={product.id} data-index={i} className="group relative flex flex-col">
            <Link to={product.link} className="block">
              <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-muted shadow-md">
                {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition duration-700 group-hover:scale-105" loading="lazy" decoding="async" />}

                {product.discounted_price && product.discounted_price < product.price && <span className="absolute left-3 top-3 rounded-lg px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] bg-primary text-white">
                      Sale
                    </span>}

                <Button variant="ghost" size="icon" aria-label="Add to wishlist" className="absolute right-3 top-3 size-9 rounded-lg bg-background/80 text-foreground opacity-0 backdrop-blur-sm transition group-hover:opacity-100 hover:bg-background" onClick={e => {
              e.preventDefault();
            }}>
                  <Heart className="size-4" />
                </Button>
              </div>
            </Link>

            <div className="mt-5 flex flex-col gap-1.5">
              <Link to={product.link}>
                <h3 className="font-default text-lg leading-snug text-foreground transition hover:text-primary">
                  {product.title}
                </h3>
              </Link>

              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Industrial Grade Material
              </p>

              <div className="mt-1 flex items-baseline gap-2">
                {product.discounted_price && product.discounted_price < product.price ? <>
                    <span className="text-sm font-bold text-primary">
                      ${product.discounted_price}
                    </span>
                    <span className="text-xs text-muted-foreground line-through">
                      ${product.price}
                    </span>
                  </> : <span className="text-sm font-bold text-foreground">
                    ${product.price}
                  </span>}
              </div>

              <div className="mt-4">
                <AddToCartButton productId={product.id} variant="outline" size="sm" className="w-full" buttonClassName="bg-primary text-white hover:bg-primary/90" text="Request Quote" showQuantity={false} />
              </div>
            </div>
          </article>)}
      </div>

      {hasNext && <div className="mt-16 flex justify-center">
          <Button onClick={async () => await loadMore()} disabled={isRefetching} variant="outline" className="rounded-lg border-primary bg-transparent px-8 py-6 font-mono text-[11px] uppercase tracking-[0.22em] text-primary hover:bg-primary hover:text-white">
            {isRefetching ? "Loading..." : "Load More"}
          </Button>
        </div>}
    </>;
}
export default function ShopContent() {
  const [filterOpen, setFilterOpen] = useState(false);
  return <section data-section-id="3795" id="shop-content" className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-12 flex flex-col gap-4 border-b border-border pb-10 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-primary">
              Swiss Solutions — Catalog
            </p>
            <h1 className="mt-3 font-default text-4xl text-foreground md:text-6xl">
              Construction Materials
            </h1>
            <p className="mt-3 max-w-xl text-sm text-muted-foreground md:text-base">
              Premium industrial materials sourced from certified manufacturers. 
              Engineered for durability and performance.
            </p>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col gap-8 lg:flex-row lg:gap-12">
            <aside className="hidden w-[260px] shrink-0 lg:block">
              <div className="sticky top-24">
                <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                  Filter Catalog
                </p>
                <ProductsFilter />
              </div>
            </aside>

            <div className="w-full flex-1">
              <div className="mb-8 flex items-center justify-between gap-4">
                <div className="lg:hidden">
                  <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="rounded-lg border-primary bg-transparent font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
                        <Filter className="mr-2 size-3.5" />
                        Filter
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-full sm:max-w-sm">
                      <SheetHeader>
                        <SheetTitle className="font-default text-2xl text-foreground">
                          Filter Catalog
                        </SheetTitle>
                      </SheetHeader>
                      <div className="overflow-y-auto px-4 pb-8">
                        <ProductsFilter />
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>

                <div className="ml-auto">
                  <ProductSortDropdown label="Sort By" />
                </div>
              </div>

              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}